// Compone almacenamiento.js (Storage) + la tabla producto_imagen: subir una
// imagen de producto son dos pasos (archivo a Storage, luego la fila que lo
// referencia), y este módulo los junta en una sola operación desde el
// punto de vista de la vista que lo llama. Separado de api/productos.js
// por responsabilidad: productos.js es la tabla `producto`, esto es la
// tabla `producto_imagen` + su archivo asociado.

import { supabaseClient } from '@plataforma/supabase-client';

import { eliminarArchivo, rutaDesdeUrlPublica, subirImagenProducto } from './almacenamiento.js';

/**
 * @param {string} empresaId
 * @param {string} productoId
 * @param {File} archivo
 * @param {{ esPrincipal?: boolean }} [opciones]
 */
export async function agregarImagenProducto(empresaId, productoId, archivo, opciones = {}) {
  const { url, error: errorSubida } = await subirImagenProducto(empresaId, productoId, archivo);
  if (errorSubida) return { data: null, error: errorSubida };

  const cliente = supabaseClient();

  if (opciones.esPrincipal) {
    // El índice único idx_producto_imagen_principal solo permite una
    // es_principal=true por producto — hay que apagar la anterior antes de
    // insertar la nueva como true, o el insert siguiente violaría el
    // índice.
    await cliente
      .from('producto_imagen')
      .update({ es_principal: false })
      .eq('producto_id', productoId);
  }

  // El trigger de límite de plan (fn_validar_limite_imagenes, ADR-0007)
  // corre dentro de este insert — si la empresa ya llegó al límite, este
  // insert falla con el mensaje de error que arma esa función, y el
  // archivo ya subido a Storage queda huérfano (aceptable: un archivo de
  // pocos KB sin referencia no tiene costo operativo real; limpiarlo
  // requeriría una transacción cross-servicio que Storage no soporta).
  return cliente
    .from('producto_imagen')
    .insert({
      empresa_id: empresaId,
      producto_id: productoId,
      url,
      es_principal: Boolean(opciones.esPrincipal),
      texto_alternativo: opciones.textoAlternativo ?? null,
    })
    .select()
    .single();
}

/** @param {string} imagenId */
export async function eliminarImagenProducto(imagenId) {
  const cliente = supabaseClient();

  const { data: imagen, error: errorLectura } = await cliente
    .from('producto_imagen')
    .select('url')
    .eq('id', imagenId)
    .single();
  if (errorLectura) return { error: errorLectura };

  const { error: errorBorradoFila } = await cliente
    .from('producto_imagen')
    .delete()
    .eq('id', imagenId);
  if (errorBorradoFila) return { error: errorBorradoFila };

  const ruta = rutaDesdeUrlPublica(imagen.url);
  if (ruta) await eliminarArchivo(ruta);

  return { error: null };
}

/**
 * @param {string} productoId
 * @param {string} imagenId
 */
export async function marcarImagenPrincipal(productoId, imagenId) {
  const cliente = supabaseClient();

  const { error: errorApagar } = await cliente
    .from('producto_imagen')
    .update({ es_principal: false })
    .eq('producto_id', productoId);
  if (errorApagar) return { error: errorApagar };

  return cliente.from('producto_imagen').update({ es_principal: true }).eq('id', imagenId);
}
